import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../repositories/admin";
import { XMarkIcon, ChevronDownIcon, ClipboardDocumentIcon, CheckIcon } from "@heroicons/react/24/outline";
import SegmentedTabs, { SegmentedTabItem } from "../ui/SegmentedTabs";
import {
  formInputClass,
  formSelectClass,
  formSelectChevronClass,
} from "../ui/formStyles";
import Button from "../ui/Button";

const TAB_ITEMS: SegmentedTabItem[] = [
  { key: "invite", label: "Invite User" },
  { key: "code", label: "Invite Code" },
];

interface Label {
  id: number;
  name: string;
}

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCodeGenerated: () => void;
}

export default function InviteUserModal({ isOpen, onClose, onCodeGenerated }: InviteUserModalProps) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [codeMessage, setCodeMessage] = useState("");
  const [labels, setLabels] = useState<Label[]>([]); // Updated type to Label[]
  const [selectedLabel, setSelectedLabel] = useState(""); // Selected label
  const [tab, setTab] = useState<"invite" | "code">("invite");
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const navigate = useNavigate();

  // Fetch labels from the backend
  useEffect(() => {
    const fetchLabels = async () => {
      try {
        const response = await adminApi.getLabels();
        setLabels(response.data.data || []); // Ensure labels are set correctly
      } catch (error) {
        console.error("Error fetching labels:", error);
        setMessage("❌ Failed to fetch labels. Please try again.");
      }
    };

    fetchLabels();
  }, []);

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleInvite = async () => {
    if (!email) {
      setMessage("⚠️ Please enter a valid email.");
      return;
    }

    if (!isValidEmail(email)) {
      setMessage("⚠️ Invalid email format. Please enter a valid email.");
      return;
    }

    if (!selectedLabel) {
      setMessage("⚠️ Please select a label.");
      return;
    }

    try {
      setLoading(true);
      setMessage("");
      await adminApi.inviteUser({ email, label: selectedLabel }); // Send email and selected label
      setMessage("✅ Invite sent successfully!");
      setEmail("");
      setSelectedLabel("");
    } catch (error: any) {
      console.error("Error sending invite:", error);
      setMessage(`❌ ${error?.response?.data?.message || "Failed to send invite. Please try again."}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCode = async () => {
    if (!selectedLabel) {
      setCodeMessage("⚠️ Please select a label.");
      return;
    }
    try {
      setLoading(true);
      setCodeMessage("");
      const response = await adminApi.createInviteCode({ label: selectedLabel });
      setInviteCode(response.data.code);
      setCodeMessage("Invite code generated!");
      if (onCodeGenerated) {
        onCodeGenerated(); // Notify parent to refresh codes
      }
    } catch (error) {
      console.error("Error generating code:", error);
      setCodeMessage("Failed to generate code. Try again.");
    } finally {
      setLoading(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative w-full max-w-lg p-6 bg-white rounded-lg shadow-lg">
        {/* Close Button */}
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 p-1 transition-colors rounded-md text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700 border border-transparent"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>

        {/* The shared underline tabs, as on /email and /transactions. */}
        <SegmentedTabs
          className="mt-6 mb-6"
          items={TAB_ITEMS}
          value={tab}
          onChange={(key) => {
            setTab(key as "invite" | "code");
            setInviteCode(null);
          }}
          ariaLabel="Invite method"
        />

        {
          tab === "invite" && (
            <>
              <h2 className="mb-2 text-lg font-semibold text-center text-black">
                Invite a User
              </h2>
              <p className="mb-6 text-sm text-center text-neutral-600">
                Select a label and enter an email address to send an invite.
              </p>

              {/* Label Dropdown */}
              <div className="relative mb-4">
                <select
                  className={formSelectClass}
                  aria-label="Label"
                  value={selectedLabel}
                  onChange={(e) => setSelectedLabel(e.target.value)}
                >
                  <option value="" disabled>
                    Select a label
                  </option>
                  {labels.map((label) => (
                    <option key={label.id} value={label.name}>
                      {label.name}
                    </option>
                  ))}
                </select>
                <div className={formSelectChevronClass}>
                  <ChevronDownIcon className="w-4 h-4" />
                </div>
              </div>

              {/* Email Input */}
              <div className="relative mb-4">
                <input
                  type="email"
                  className={formInputClass}
                  placeholder="Enter user email..."
                  aria-label="User email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              {/* Invite Button */}
              <Button
                className="w-full mt-4"
                onClick={handleInvite}
                loading={loading}
              >
                {loading ? "Sending..." : "Send Invite"}
              </Button>
            </>
          )
        }

        {
          tab === "code" && (
            <>
              <h2 className="mb-2 text-lg font-semibold text-center text-black">Invite Code</h2>
              <p className="mb-6 text-sm text-center text-neutral-600">
                Select a label and click Generate Trial Code.
              </p>

              {/* Label Dropdown */}
              <div className="relative mb-4">
                <select
                  className={formSelectClass}
                  aria-label="Label"
                  value={selectedLabel}
                  onChange={(e) => setSelectedLabel(e.target.value)}
                >
                  <option value="" disabled>
                    Select a label
                  </option>
                  {labels.map((label) => (
                    <option key={label.id} value={label.name}>
                      {label.name}
                    </option>
                  ))}
                </select>
                <div className={formSelectChevronClass}>
                  <ChevronDownIcon className="w-4 h-4" />
                </div>
              </div>

              {/* Show generated code — a tinted callout, since the code is the result the admin came for */}
              {inviteCode && (
                <div className="flex items-center justify-between gap-3 px-4 py-3 mt-4 border rounded-lg border-primary-200 bg-primary-50">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-lg truncate text-primary-900 select-all">
                      {inviteCode}
                    </span>
                    {/* Was checking `message`, which this button never sets — so the tick never appeared. */}
                    {codeMessage === "Copied to clipboard!" && (
                      <CheckIcon className="w-5 h-5 shrink-0 text-success-600" />
                    )}
                  </div>
                  <Button
                    size="sm"
                    className="shrink-0"
                    onClick={() => {
                      navigator.clipboard.writeText(inviteCode);
                      setCodeMessage("Copied to clipboard!");
                      setTimeout(() => setCodeMessage(""), 1500); // Hide check after 1.5s
                    }}
                    title="Copy to clipboard"
                  >
                    <ClipboardDocumentIcon className="w-4 h-4" />
                    Copy
                  </Button>
                </div>
              )}

              <Button
                className="w-full mt-4"
                onClick={handleGenerateCode}
                loading={loading}
              >
                {loading ? "Generating..." : "Generate Trial Code"}
              </Button>
            </>
          )
        }

        {/* Response Message */}



        {tab === "invite" && (
          message && (
            <p className="mt-4 text-sm font-medium text-center text-neutral-700">{message}</p>
          )
        )}

        {tab === "code" && (
          codeMessage && (
            <p className="mt-4 text-sm font-medium text-center text-neutral-700">{codeMessage}</p>
          )
        )}
      </div>
    </div>
  );
}